import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, ChevronDown } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import logo from "@/assets/photohawk-logo.png";
import logoWhite from "@/assets/photohawk-logo-white.png";
import { useCognitoAuth } from "@/hooks/useCognitoAuth";
import { SiteLightHeader, useSiteLight } from "@/components/SiteLight";
import { useTheme } from "@/hooks/useTheme";
import { EarningsMenuItem } from "@/components/earnings/EarningsSignpost";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface AppHeaderProps {
  variant?: "marketing" | "app";
  workspaceName?: string;
}

const AppHeader = ({ variant = "marketing", workspaceName }: AppHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, signOut } = useCognitoAuth();
  const { theme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isAppVariant = variant === "app";
  // The new site's header on the public pages where the new site is on (staging until launch).
  const siteLight = useSiteLight();
  const isDark = theme === "dark";

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const { t } = useTranslation();

  const marketingNavItems = [
    { label: t("nav.home"), href: "/", isRoute: true },
    { label: t("nav.findPhotos"), href: "/find-your-photos", isRoute: true },
    { label: t("nav.pricing"), href: "/pricing", isRoute: true },
  ];

  const appNavItems = [
    { label: t("nav.workspaces"), href: "/admin/workspaces", isRoute: true },
  ];

  const navItems = isAppVariant ? appNavItems : marketingNavItems;

  // Workspace/admin pages get a minimal, blur-free bar: just the logo and the
  // account menu. Navigation lives in the workspace sidebar / bottom nav, so
  // there's no hamburger or workspace-name breadcrumb here.
  if (isAppVariant) {
    return (
      <header className="h-14 shrink-0 bg-hawk-navy shadow-sm">
        <div className="h-full container mx-auto px-4 flex items-center justify-between">
          <Link to="/admin/workspaces" className="flex items-center">
            <img src={logoWhite} alt="Photohawk" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2">
              <LanguageSelector />
            </div>
            {isAuthenticated && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="heroOutline" size="sm" className="gap-2">
                    <span className="max-w-[120px] truncate">
                      {user?.signInDetails?.loginId || user?.username || "Account"}
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate("/admin/workspaces")}>
                    {t("nav.workspaces")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/account")}>
                    Account
                  </DropdownMenuItem>
                  <EarningsMenuItem />
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t("nav.signOut")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>
    );
  }

  if (siteLight) return <SiteLightHeader />;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isAppVariant
          ? "bg-hawk-navy/95 backdrop-blur-xl shadow-md py-3"
          : "bg-hawk-navy/90 backdrop-blur-xl py-4"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={logoWhite} 
              alt="Photohawk" 
              className="h-9 w-auto transition-opacity duration-300"
            />
            {workspaceName && (
              <>
                <span className="text-white/40">/</span>
                <span className="text-white font-medium text-sm truncate max-w-[150px]">
                  {workspaceName}
                </span>
              </>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = item.isRoute
                ? location.pathname === item.href
                : location.hash === item.href;

              return item.isRoute ? (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "text-white bg-white/10"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.href}
                  href={item.href}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-white/70 hover:text-white hover:bg-white/5"
                >
                  {item.label}
                </a>
              );
            })}
          </nav>

          {/* Right side actions */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSelector />
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="heroOutline"
                    size="sm"
                    className="gap-2"
                  >
                    <span className="max-w-[120px] truncate">
                      {user?.signInDetails?.loginId || user?.username || "Account"}
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate("/admin/workspaces")}>
                    {t("nav.workspaces")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/account")}>
                    Account
                  </DropdownMenuItem>
                  <EarningsMenuItem />
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t("nav.signOut")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="heroOutline"
                  size="sm"
                  onClick={() => navigate("/login")}
                >
                  {t("nav.logIn")}
                </Button>
                <Button 
                  variant="hero" 
                  size="sm"
                  onClick={() => navigate("/register")}
                >
                  {t("nav.signUpFree")}
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="text-white" size={24} />
            ) : (
              <Menu className="text-white" size={24} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 animate-fade-in">
            <nav className="flex flex-col gap-4">
              {navItems.map((item) => (
                item.isRoute ? (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="text-sm font-medium text-white"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    key={item.href}
                    href={item.href}
                    className="text-sm font-medium text-white"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                )
              ))}
              <div className="flex flex-col gap-2 pt-4 border-t border-white/20">
                {isAuthenticated ? (
                  <>
                    <Button 
                      variant="heroOutline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => {
                        navigate("/admin/workspaces");
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Dashboard
                    </Button>
                    <Button 
                      variant="heroOutline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => {
                        handleSignOut();
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="heroOutline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => {
                        navigate("/login");
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Log In
                    </Button>
                    <Button 
                      variant="hero" 
                      size="sm" 
                      className="w-full"
                      onClick={() => {
                        navigate("/register");
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Sign Up Free
                    </Button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default AppHeader;
